<template>
  <div>
    <Header title="Gestion des villes" />
    <div class="villes-view">
      <div class="container">
        <!-- En-tête avec bouton retour -->
        <div class="view-header">
          <router-link to="/parametres" class="back-button">
            <span class="material-icons">arrow_back</span>
            Retour aux paramètres
          </router-link>
          <h1>Gestion des villes</h1>
          <div class="header-actions">
            <button class="btn btn-primary" @click="openAddVilleModal">
              <span class="material-icons">add</span>
              Nouvelle ville
            </button>
          </div>
        </div>

        <!-- Section de recherche -->
        <div class="search-section">
          <div class="search-input">
            <span class="material-icons">search</span>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Rechercher une ville..." 
              @input="filterVilles"
            />
          </div>
        </div>

        <!-- Liste des villes -->
        <div class="content-container">
          <div v-if="loading" class="loading-indicator">
            <div class="spinner"></div>
            <p>Chargement des villes...</p>
          </div>
          
          <div v-else-if="filteredVilles.length === 0" class="no-results">
            <span class="material-icons">search_off</span>
            <p v-if="searchQuery">Aucune ville trouvée pour "{{ searchQuery }}"</p>
            <p v-else>Aucune ville disponible. Veuillez en ajouter une.</p>
          </div>

          <div v-else class="villes-list">
            <div
              v-for="ville in filteredVilles"
              :key="ville.id"
              class="ville-item"
              :class="{ 'selected': selectedVille && selectedVille.id === ville.id }"
              @click="selectVille(ville)"
            >
              <div class="ville-info">
                <span class="ville-icon material-icons">apartment</span>
                <div class="ville-details">
                  <h3>{{ ville.name }}</h3>
                  <p>
                    <span v-if="ville.province">Province: {{ ville.province.name }}</span>
                    <span v-else>Province non définie</span>
                    <span v-if="ville.province" class="pays-info"> | Pays: {{ ville.province.country.name }}</span>
                  </p>
                </div>
              </div>
              <div class="ville-actions">
                <button class="btn-icon" @click.stop="editVille(ville)">
                  <span class="material-icons">edit</span>
                </button>
                <button class="btn-icon" @click.stop="confirmDeleteVille(ville)">
                  <span class="material-icons">delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Détails de la ville sélectionnée -->
        <div v-if="selectedVille" class="ville-details-panel">
          <div class="panel-header">
            <h3>Détails de la ville</h3>
            <button class="btn-close" @click="selectedVille = null">
              <span class="material-icons">close</span>
            </button>
          </div>
          <div class="panel-content">
            <div class="detail-field">
              <span class="field-label">Nom:</span>
              <span class="field-value">{{ selectedVille.name }}</span>
            </div>
            <div class="detail-field">
              <span class="field-label">Province:</span>
              <span class="field-value">{{ selectedVille.province.name || 'Non définie' }}</span>
            </div>
            <div class="detail-field">
              <span class="field-label">Pays:</span>
              <span class="field-value">{{ selectedVille.province.country.name || 'Non défini' }}</span>
            </div>
            <div class="detail-field">
              <span class="field-label">Créée le:</span>
              <span class="field-value">{{ formatDate(selectedVille.createdAt) }}</span>
            </div>
            <div class="detail-field">
              <span class="field-label">Modifiée le:</span>
              <span class="field-value">{{ formatDate(selectedVille.updatedAt) }}</span>
            </div>
            
            <div class="sites-section">
              <h4>Sites de pointage</h4>
              <div v-if="loadingSites" class="loading-indicator">
                <div class="spinner"></div>
                <p>Chargement des sites...</p>
              </div>
              <div v-else-if="sitesList.length === 0" class="no-results">
                <p>Aucun site de pointage disponible pour cette ville.</p>
              </div>
              <div v-else class="sites-list">
                <!-- Barre de recherche des sites -->
                <div class="sites-search">
                  <div class="search-input-sites">
                    <span class="material-icons">search</span>
                    <input 
                      type="text" 
                      v-model="siteSearchQuery" 
                      placeholder="Rechercher un site de pointage..." 
                    />
                  </div>
                </div>
                
                <!-- Liste filtrée des sites -->
                <div class="sites-container">
                  <div v-for="site in filteredSites" :key="site.id" class="site-item" @click="openSiteModal(site)">
                    <div class="site-header">
                      <span class="site-icon material-icons">location_on</span>
                      <div class="site-title">
                        <h5>{{ site.name }}</h5>
                        <span class="site-status">Cliquez pour voir sur la carte</span>
                      </div>
                    </div>
                    <div class="site-details">
                      <div class="site-detail">
                        <span class="detail-label">Code:</span>
                        <span class="detail-value">{{ site.code }}</span>
                      </div>
                      <div class="site-detail">
                        <span class="detail-label">Adresse:</span>
                        <span class="detail-value">{{ site.address }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal pour l'ajout/modification de ville -->
    <dialog ref="villeModal" class="ville-modal">
      <div class="modal-header">
        <h3>{{ currentAction === 'create' ? 'Ajouter une ville' : 'Modifier la ville' }}</h3>
        <button class="btn-close" @click="closeModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveVille" id="villeForm">
          <div class="form-group">
            <label for="nom">Nom de la ville*</label>
            <input 
              type="text" 
              id="nom" 
              v-model="formData.name" 
              required 
              :class="{ 'error': formErrors.name }"
            />
            <span class="error-message" v-if="formErrors.name">{{ formErrors.name }}</span>
          </div>
          <div class="form-group">
            <label for="province_id">Province*</label>
            <select 
              id="province_id" 
              v-model="formData.province_id"
              required
              :class="{ 'error': formErrors.province_id }"
            >
              <option value="">-- Sélectionner une province --</option>
              <optgroup 
                v-for="country in provincesByCountry" 
                :key="country.id"
                :label="country.name"
              >
                <option 
                  v-for="province in country.provinces" 
                  :key="province.id" 
                  :value="province.id"
                >
                  {{ province.name }}
                </option>
              </optgroup>
            </select>
            <span class="error-message" v-if="formErrors.province_id">{{ formErrors.province_id }}</span>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeModal" :disabled="savingVille">Annuler</button>
        <button class="btn btn-primary" form="villeForm" :disabled="savingVille">
          <div v-if="savingVille" class="btn-spinner"></div>
          <span v-if="savingVille">Traitement en cours...</span>
          <span v-else>{{ currentAction === 'create' ? 'Ajouter' : 'Enregistrer' }}</span>
        </button>
      </div>
    </dialog>

    <!-- Modal de confirmation de suppression -->
    <dialog ref="confirmDeleteModal" class="confirm-modal">
      <div class="modal-header">
        <h3>Confirmer la suppression</h3>
        <button class="btn-close" @click="closeConfirmModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <p>Êtes-vous sûr de vouloir supprimer la ville "{{ villeToDelete?.name }}" ?</p>
        <p class="warning">Cette action est irréversible et peut affecter les agents associés à cette ville.</p>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="closeConfirmModal" :disabled="deletingVille">Annuler</button>
        <button class="btn btn-danger" @click="deleteVille" :disabled="deletingVille">
          <div v-if="deletingVille" class="btn-spinner"></div>
          <span v-if="deletingVille">Suppression en cours...</span>
          <span v-else>Supprimer</span>
        </button>
      </div>
    </dialog>

    <!-- Notification modale centrée -->
    <div 
      v-if="notification.show" 
      class="notification" 
      :class="notification.type"
    >
      <div class="notification-content">
        <div class="notification-icon">
          <span class="material-icons">
            {{ notification.type === 'success' ? 'check_circle' : 
               notification.type === 'error' ? 'error' : 
               notification.type === 'info' ? 'info' : 'warning' }}
          </span>
        </div>
        <div class="notification-text">
          <h4>{{ notification.title }}</h4>
          <p>{{ notification.message }}</p>
        </div>
      </div>
      <div class="notification-actions">
        <button class="btn btn-primary" @click="notification.show = false">
          OK
        </button>
      </div>
    </div>
    
    <!-- Modal Leaflet pour afficher la carte du site -->
    <dialog ref="siteModal" class="site-modal">
      <div class="modal-header">
        <h3>Localisation du site</h3>
        <button class="btn-close" @click="closeSiteModal">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" v-if="selectedSite">
        <div class="site-info">
          <h4>{{ selectedSite.name }}</h4>
          <p><strong>Code:</strong> {{ selectedSite.code }}</p>
          <p><strong>Adresse:</strong> {{ selectedSite.address }}</p>
          <div class="location-controls">
            <button 
              class="btn btn-location" 
              @click="toggleUserLocation" 
              :disabled="loadingLocation"
              :class="{ 'active': userLocationEnabled }"
            >
              <span v-if="loadingLocation" class="material-icons spinning">hourglass_empty</span>
              <span v-else class="material-icons">{{ userLocationEnabled ? 'my_location' : 'location_searching' }}</span>
              {{ loadingLocation ? 'Localisation...' : (userLocationEnabled ? 'Position activée' : 'Ma position') }}
            </button>
            <button 
              v-if="userLocationEnabled && userPosition" 
              class="btn btn-route" 
              @click="calculateRoute"
              :disabled="loadingRoute"
            >
              <span v-if="loadingRoute" class="material-icons spinning">hourglass_empty</span>
              <span v-else class="material-icons">directions</span>
              {{ loadingRoute ? 'Calcul...' : 'Itinéraire' }}
            </button>
            <button 
              v-if="routeDisplayed" 
              class="btn btn-clear" 
              @click="clearRoute"
            >
              <span class="material-icons">clear</span>
              Effacer
            </button>
          </div>
          <div v-if="routeInfo" class="route-info">
            <div class="route-stats">
              <span class="stat-item">
                <span class="material-icons">straighten</span>
                {{ routeInfo.distance }}
              </span>
              <span class="stat-item">
                <span class="material-icons">schedule</span>
                {{ routeInfo.duration }}
                <span class="material-icons car-icon" title="Temps calculé pour les véhicules">directions_car</span>
              </span>
            </div>
          </div>
        </div>
        <div id="leaflet-map" class="leaflet-map"></div>
      </div>
    </dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import api from '../services/api';
import Header from '../components/Header.vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';

const router = useRouter();
const loading = ref(false);
const villes = ref([]);
const filteredVilles = ref([]);
const selectedVille = ref(null);
const searchQuery = ref('');
const provincesList = ref([]);
const paysList = ref([]);
const villeToDelete = ref(null);
const sitesList = ref([]);
const loadingSites = ref(false);
const savingVille = ref(false);
const deletingVille = ref(false);
const siteModal = ref(null);
const selectedSite = ref(null);
const siteSearchQuery = ref('');
const userLocationEnabled = ref(false);
const userPosition = ref(null);
const loadingLocation = ref(false);
const loadingRoute = ref(false);
const routeDisplayed = ref(false);
const routeInfo = ref(null);
let userMarker = null;
let routeControl = null;

// Modals
const villeModal = ref(null);
const confirmDeleteModal = ref(null);

// État du formulaire
const formData = reactive({
  name: '',
  province_id: ''
});
const formErrors = reactive({
  name: '',
  province_id: ''
});

// État de l'action courante (create, update, delete)
const currentAction = ref('create');

// Notification
const notification = reactive({
  show: false,
  type: 'info',
  title: '',
  message: '',
  duration: 5000
});

onMounted(async () => {
  await Promise.all([loadVilles(), loadProvinces(), loadPays()]);
});

async function loadVilles() {
  loading.value = true;
  try {
    const response = await api.getVilles();
    if (response.data && response.data.success) {
      villes.value = response.data.data || [];
      filteredVilles.value = [...villes.value];
    }
  } catch (error) {
    console.error('Erreur lors du chargement des villes:', error);
    showNotification('error', 'Erreur', 'Impossible de charger les villes.');
  } finally {
    loading.value = false;
  }
}

async function loadProvinces() {
  try {
    const response = await api.getProvinces();
    if (response.data && response.data.success) {
      provincesList.value = response.data.data || [];
      // Organiser les provinces par pays
      organizeProvincesByCountry();
    }
  } catch (error) {
    console.error('Erreur lors du chargement des provinces:', error);
    showNotification('error', 'Erreur', 'Impossible de charger la liste des provinces.');
  }
}

// Fonction pour organiser les provinces par pays
const provincesByCountry = ref([]);

function organizeProvincesByCountry() {
  // Créer un objet pour regrouper les provinces par pays
  const groupedProvinces = {};
  
  // Regrouper les provinces par pays_id
  provincesList.value.forEach(province => {
    if (province.country && province.country.id) {
      const countryId = province.country.id;
      if (!groupedProvinces[countryId]) {
        groupedProvinces[countryId] = {
          id: countryId,
          name: province.country.name,
          provinces: []
        };
      }
      groupedProvinces[countryId].provinces.push(province);
    }
  });
  
  // Convertir l'objet en tableau pour l'affichage
  provincesByCountry.value = Object.values(groupedProvinces);
  
  // Trier les pays par nom
  provincesByCountry.value.sort((a, b) => a.name.localeCompare(b.name));
  
  // Trier les provinces par nom dans chaque pays
  provincesByCountry.value.forEach(country => {
    country.provinces.sort((a, b) => a.name.localeCompare(b.name));
  });
}

async function loadPays() {
  try {
    const response = await api.getCountries();
    if (response.data && response.data.success) {
      paysList.value = response.data.data || [];
    }
  } catch (error) {
    //console.error('Erreur lors du chargement des pays:', error);
  }
}

function filterVilles() {
  if (!searchQuery.value.trim()) {
    filteredVilles.value = [...villes.value];
    return;
  }
  
  const query = searchQuery.value.toLowerCase();
  filteredVilles.value = villes.value.filter(ville => {
    const nomMatch = ville.name.toLowerCase().includes(query);
    
    let provinceMatch = false;
    const province = provincesList.value.find(p => p.id === ville.province_id);
    if (province) {
      provinceMatch = province.name.toLowerCase().includes(query);
      
      let paysMatch = false;
      const pays = paysList.value.find(p => p.id === province.pays_id);
      if (pays) {
        paysMatch = pays.name.toLowerCase().includes(query);
      }
      
      return nomMatch || provinceMatch || paysMatch;
    }
    
    return nomMatch;
  });
}

function getProvinceName(provinceId) {
  const province = provincesList.value.find(p => p.id === provinceId);
  return province ? province.nom : null;
}

function getPaysName(paysId) {
  const pays = paysList.value.find(p => p.id === paysId);
  return pays ? pays.nom : null;
}

function getPaysNameByProvince(provinceId) {
  const province = provincesList.value.find(p => p.id === provinceId);
  if (province && province.pays_id) {
    return getPaysName(province.pays_id);
  }
  return null;
}

function selectVille(ville) {
  selectedVille.value = ville;
  loadSitesForVille(ville.id);
}

async function loadSitesForVille(villeId) {
  loadingSites.value = true;
  sitesList.value = [];
  try {
    const response = await api.getSitesByVilleId(villeId);
    if (response.data && response.data.data) {
      sitesList.value = response.data.data;
    }
  } catch (error) {
    console.error('Erreur lors du chargement des sites de pointage:', error);
    showNotification('error', 'Erreur', 'Impossible de charger les sites de pointage pour cette ville.');
  } finally {
    loadingSites.value = false;
  }
}

function openAddVilleModal() {
  resetForm();
  currentAction.value = 'create';
  villeModal.value.showModal();
}

function editVille(ville) {
  resetForm();
  currentAction.value = 'update';
  formData.name = ville.name;
  formData.province_id = ville.province?.id;
  formData.id = ville.id;
  villeModal.value.showModal();
}

function confirmDeleteVille(ville) {
  villeToDelete.value = ville;
  confirmDeleteModal.value.showModal();
}

function closeModal() {
  villeModal.value.close();
}

function closeConfirmModal() {
  confirmDeleteModal.value.close();
}

function resetForm() {
  formData.id = null;
  formData.name = '';
  formData.province_id = '';
  Object.keys(formErrors).forEach(key => formErrors[key] = '');
}

async function saveVille() {
  if (!validateForm()) return;
  
  savingVille.value = true;
  
  try {
    let response;
    
    if (currentAction.value === 'create') {
      response = await api.createVille(formData);
      if (response.data && response.data.success) {
        showNotification('success', 'Succès', 'Ville créée avec succès');
      }
    } else {
      response = await api.updateVille(formData.id, formData);
      if (response.data && response.data.success) {
        showNotification('success', 'Succès', 'Ville modifiée avec succès');
      }
    }
    
    await loadVilles();
    closeModal();
    
    if (selectedVille.value && selectedVille.value.id === formData.id) {
      const updatedVille = villes.value.find(v => v.id === formData.id);
      if (updatedVille) {
        selectedVille.value = updatedVille;
      }
    }
    
  } catch (error) {
    console.error('Erreur lors de la sauvegarde de la ville:', error);
    showNotification('error', 'Erreur', `Impossible de sauvegarder la ville: ${error.message}`);
  } finally {
    savingVille.value = false;
  }
}

async function deleteVille() {
  if (!villeToDelete.value) return;
  
  deletingVille.value = true;
  
  try {
    const response = await api.deleteVille(villeToDelete.value.id);
    if (response.data && response.data.success) {
      showNotification('success', 'Suppression', 'Ville supprimée avec succès');
      
      if (selectedVille.value && selectedVille.value.id === villeToDelete.value.id) {
        selectedVille.value = null;
      }
      
      await loadVilles();
    }
  } catch (error) {
    console.error('Erreur lors de la suppression de la ville:', error);
    showNotification('error', 'Erreur', `Impossible de supprimer la ville: ${error.message}`);
  } finally {
    deletingVille.value = false;
  }
  
  closeConfirmModal();
}

function validateForm() {
  let isValid = true;
  
  if (!formData.name.trim()) {
    formErrors.name = 'Le nom est requis';
    isValid = false;
  } else {
    formErrors.name = '';
  }
  
  if (!formData.province_id) {
    formErrors.province_id = 'La province est requise';
    isValid = false;
  } else {
    formErrors.province_id = '';
  }
  
  return isValid;
}

function showNotification(type, title, message) {
  notification.show = true;
  notification.type = type;
  notification.title = title;
  notification.message = message;
  
  setTimeout(() => {
    notification.show = false;
  }, notification.duration);
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}

// Computed property pour filtrer les sites
const filteredSites = computed(() => {
  if (!siteSearchQuery.value.trim()) {
    return sitesList.value;
  }
  
  const query = siteSearchQuery.value.toLowerCase();
  return sitesList.value.filter(site => {
return (site.name && site.name.toLowerCase().includes(query)) ||
           (site.code && site.code.toLowerCase().includes(query)) ||
           (site.address && site.address.toLowerCase().includes(query));
  });
});

// Géolocalisation et calcul d'itinéraire
function toggleUserLocation() {
  if (!navigator.geolocation) {
    showNotification('error', 'Géolocalisation non prise en charge', 'Votre navigateur ne prend pas en charge la géolocalisation.');
    return;
  }

  if (userLocationEnabled.value) {
    disableUserLocation();
    return;
  }

  loadingLocation.value = true;
  
  // Options pour une géolocalisation plus précise
  const geoOptions = {
    enableHighAccuracy: true,  // Demander la plus haute précision possible
    timeout: 20000,           // Timeout de 20 secondes
    maximumAge: 60000         // Accepter une position de moins d'1 minute
  };
  
  navigator.geolocation.getCurrentPosition(
    (position) => {
      console.log('Position obtenue:', {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: new Date(position.timestamp).toLocaleString()
      });
      
      userPosition.value = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      };
      userLocationEnabled.value = true;
      showUserMarker();
      loadingLocation.value = false;
    },
    (error) => {
      console.error('Erreur de géolocalisation:', error);
      let errorMessage = "Impossible d'obtenir votre position.";
      
      switch(error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = "L'accès à la géolocalisation a été refusé.";
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = "Les informations de localisation ne sont pas disponibles.";
          break;
        case error.TIMEOUT:
          errorMessage = "La demande de géolocalisation a expiré.";
          break;
      }
      
      showNotification('error', 'Erreur de géolocalisation', errorMessage);
      loadingLocation.value = false;
    },
    geoOptions
  );
}

function disableUserLocation() {
  userLocationEnabled.value = false;
  userPosition.value = null;
  if (userMarker) {
    map.removeLayer(userMarker);
  }
}

function showUserMarker() {
  if (!userPosition.value) return;

  // Créer un marqueur pour la position de l'utilisateur avec couleur turquoise
  const userIcon = L.divIcon({
    html: `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="#30C7B5" stroke="white" stroke-width="2"/>
        <circle cx="12" cy="12" r="4" fill="white"/>
      </svg>
    `,
    className: 'user-location-marker',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });

  userMarker = L.marker([userPosition.value.latitude, userPosition.value.longitude], { icon: userIcon })
    .addTo(map)
    .bindPopup('Votre position actuelle')
    .openPopup();
  
  // Ajuster la vue pour inclure les deux marqueurs
  if (selectedSite.value) {
    const group = new L.featureGroup([userMarker, map.eachLayer(layer => {
      if (layer instanceof L.Marker && layer !== userMarker) {
        return layer;
      }
    })]);
    map.fitBounds(group.getBounds().pad(0.1));
  }
}

function calculateRoute() {
  if (!userPosition.value || !selectedSite.value) {
    showNotification('error', 'Erreur', 'Position utilisateur ou site manquant.');
    return;
  }

  loadingRoute.value = true;

  routeControl = L.Routing.control({
    waypoints: [
      L.latLng(userPosition.value.latitude, userPosition.value.longitude),
      L.latLng(parseFloat(selectedSite.value.latitude), parseFloat(selectedSite.value.longitude))
    ],
    routeWhileDragging: false,
    createMarker: function() { return null; }
  })
  .on('routesfound', function(e) {
    const route = e.routes[0];
    routeInfo.value = {
      distance: (route.summary.totalDistance / 1000).toFixed(1) + ' km',
      duration: Math.round(route.summary.totalTime / 60) + ' min'
    };
    routeDisplayed.value = true;
  })
  .on('routingerror', function() {
    showNotification('error', 'Erreur de routage', 'Impossible de calculer l\'itinéraire.');
  })
  .addTo(map);

  loadingRoute.value = false;
}

function displayRoute(route) {
  const coordinates = route.geometry.coordinates.map(coord => [coord[1], coord[0]]);
  
  routeControl = L.polyline(coordinates, {
    color: '#30C7B5',
    weight: 4,
    opacity: 0.8
  }).addTo(map);
  
  // Ajuster la vue pour montrer tout l'itinéraire
  map.fitBounds(routeControl.getBounds().pad(0.1));
}

function drawStraightLine() {
  const coords = [
    [userPosition.value.latitude, userPosition.value.longitude],
    [parseFloat(selectedSite.value.latitude), parseFloat(selectedSite.value.longitude)]
  ];
  
  routeControl = L.polyline(coords, {
    color: '#30C7B5',
    weight: 4,
    opacity: 0.6,
    dashArray: '10, 10'
  }).addTo(map);
  
  routeDisplayed.value = true;
  
  // Calculer la distance à vol d'oiseau
  const distance = map.distance(
    [userPosition.value.latitude, userPosition.value.longitude],
    [parseFloat(selectedSite.value.latitude), parseFloat(selectedSite.value.longitude)]
  );
  
  routeInfo.value = {
    distance: (distance / 1000).toFixed(1) + ' km (à vol d\'oiseau)',
    duration: 'Estimation non disponible'
  };
}

function clearRoute() {
  if (routeControl) {
    map.removeControl(routeControl);
    routeControl = null;
  }
  routeDisplayed.value = false;
  routeInfo.value = null;
  
  // Supprimer le panneau de directions Leaflet Routing Machine
  const routingContainer = document.querySelector('.leaflet-routing-container');
  if (routingContainer) {
    routingContainer.remove();
  }
}

// Fonctions pour la modal de carte
function openSiteModal(site) {
  selectedSite.value = site;
  siteModal.value.showModal();
  
  // Attendre que la modal soit affichée avant d'initialiser la carte
  nextTick(() => {
    initLeafletMap();
  });
}

function closeSiteModal() {
  // Réinitialiser tous les états de géolocalisation et d'itinéraire
  resetLocationStates();
  
  siteModal.value.close();
  selectedSite.value = null;
}

// Fonction pour réinitialiser tous les états liés à la géolocalisation
function resetLocationStates() {
  userLocationEnabled.value = false;
  userPosition.value = null;
  loadingLocation.value = false;
  loadingRoute.value = false;
  routeDisplayed.value = false;
  routeInfo.value = null;
  
  // Supprimer les marqueurs et itinéraires de la carte si ils existent
  if (userMarker && map) {
    map.removeLayer(userMarker);
    userMarker = null;
  }
  
  if (routeControl && map) {
    map.removeLayer(routeControl);
    routeControl = null;
  }
}

let map = null;

function initLeafletMap() {
  if (!selectedSite.value || !selectedSite.value.latitude || !selectedSite.value.longitude) {
    return;
  }
  
  // Détruire la carte existante si elle existe
  if (map) {
    map.remove();
    map = null;
  }
  
  const mapContainer = document.getElementById('leaflet-map');
  if (!mapContainer) return;
  
  // Créer la carte
  map = L.map('leaflet-map').setView([parseFloat(selectedSite.value.latitude), parseFloat(selectedSite.value.longitude)], 15);
  
  // Ajouter la couche de tuiles
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors'
  }).addTo(map);
  
  // Créer une icône personnalisée SVG élégante
  const customIcon = L.divIcon({
    html: `
      <svg width="32" height="42" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg">
        <filter id="shadow-${selectedSite.value.id}" height="130%">
          <feDropShadow dx="0" dy="3" stdDeviation="3" flood-color="rgba(0,0,0,0.35)"/>
        </filter>
        <path d="M16 0C7.163 0 0 7.163 0 16C0 19.68 1.104 23.08 3.06 25.776C6.57 31.326 16 42 16 42C16 42 25.43 31.326 28.94 25.776C30.896 23.08 32 19.68 32 16C32 7.163 24.837 0 16 0Z" fill="#30C7B5" filter="url(#shadow-${selectedSite.value.id})"/>
        <circle cx="16" cy="16" r="8" fill="white"/>
        <circle cx="16" cy="16" r="6" fill="#155751"/>
        <circle cx="16" cy="16" r="3" fill="white"/>
      </svg>
    `,
    className: 'custom-leaflet-marker',
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -38]
  });
  
  // Ajouter le marqueur
  L.marker([parseFloat(selectedSite.value.latitude), parseFloat(selectedSite.value.longitude)], { icon: customIcon })
    .addTo(map)
    .bindPopup(`
      <div class="popup-content">
        <h4>${selectedSite.value.name}</h4>
        <p><strong>Code:</strong> ${selectedSite.value.code}</p>
        <p><strong>Adresse:</strong> ${selectedSite.value.address}</p>
      </div>
    `)
    .openPopup();
    
  // Forcer le redimensionnement de la carte après un délai
  setTimeout(() => {
    if (map) {
      map.invalidateSize();
    }
  }, 100);
}
</script>

<style scoped>
.villes-view {
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
  margin: 0 auto;
}

.container {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
}

@media (min-width: 992px) {
  .container {
    grid-template-columns: 2fr 1fr;
  }
  
  .view-header, .search-section, .loading-indicator, .no-results {
    grid-column: span 2;
  }
}

/* En-tête */
.view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.view-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.back-button {
  display: flex;
  align-items: center;
  color: var(--turquoise-primary);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.back-button:hover {
  color: var(--turquoise-deep);
}

.back-button .material-icons {
  margin-right: 5px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Section de recherche */
.search-section {
  margin-bottom: 20px;
}

.search-input {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 10px 15px;
}

.search-input .material-icons {
  color: #777;
  margin-right: 10px;
}

.search-input input {
  border: none;
  background: transparent;
  flex: 1;
  font-size: 16px;
  outline: none;
}

/* Liste des villes */
.villes-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ville-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-radius: 8px;
  background-color: #f9f9f9;
  border-left: 4px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ville-item:hover {
  background-color: #f0f0f0;
}

.ville-item.selected {
  border-left-color: var(--turquoise-primary);
  background-color: var(--turquoise-pale);
  box-shadow: 0 2px 8px rgba(48, 199, 181, 0.15);
}

.ville-info {
  display: flex;
  align-items: center;
}

.ville-icon {
  color: var(--turquoise-primary);
  margin-right: 15px;
  font-size: 24px;
  transition: transform 0.2s ease;
}

.ville-item:hover .ville-icon {
  transform: scale(1.1);
}

.ville-details h3 {
  margin: 0 0 5px;
  font-size: 16px;
  font-weight: 500;
}

.ville-details p {
  margin: 0;
  color: #777;
  font-size: 14px;
}

.pays-info {
  margin-left: 5px;
  font-style: italic;
}

.ville-actions {
  display: flex;
  gap: 10px;
}

.btn-icon {
  border: none;
  background: none;
  color: #777;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.btn-icon:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: #333;
}

/* Panneau de détails */
.ville-details-panel {
  background-color: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  padding: 18px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
}

.panel-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.panel-content {
  padding: 15px;
}

.detail-field {
  margin-bottom: 15px;
}

.field-label {
  font-weight: 600;
  color: #555;
  display: inline-block;
  width: 100px;
}

.field-value {
  color: #333;
}

/* Styles pour la section des sites de pointage */
.sites-section {
  margin-top: 25px;
  border-top: 1px solid #e5e7eb;
  padding-top: 20px;
}

.sites-section h4 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.sites-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-height: 400px;
  overflow-y: auto;
}

.site-item {
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.site-item:hover {
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.site-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.site-icon {
  color: var(--turquoise-primary);
  margin-right: 10px;
  font-size: 20px;
}

.site-header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.site-details {
  padding-left: 30px;
}

.site-detail {
  margin-bottom: 8px;
}

.detail-label {
  font-weight: 500;
  color: #555;
  display: inline-block;
  width: 80px;
}

.detail-value {
  color: #333;
}

.no-results {
  padding: 20px;
  text-align: center;
  color: #666;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px dashed #e5e7eb;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(48, 199, 181, 0.2);
  border-top-color: var(--turquoise-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Indicateur de chargement */
.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(48, 199, 181, 0.1);
  border-radius: 50%;
  border-top: 4px solid var(--turquoise-primary);
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translate(-50%, -60%); }
  to { opacity: 1; transform: translate(-50%, -50%); }
}

/* Pas de résultats */
.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: #777;
}

.no-results .material-icons {
  font-size: 48px;
  margin-bottom: 15px;
  color: #aaa;
}

/* Modal */
.confirm-modal {
  border: none;
  border-radius: 12px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25), 0 5px 15px rgba(0, 0, 0, 0.15);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  animation: fadeIn 0.3s ease-out;
  overflow: hidden;
}

.confirm-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
}

.confirm-modal::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: 0.025em;
}

.modal-body {
  padding: 24px;
  background: linear-gradient(to bottom, #ffffff, #f9fafb);
}

.modal-footer {
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.btn-close {
  background: none;
  border: none;
  cursor: pointer;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background-color: rgba(255, 255, 255, 0.15);
  transform: rotate(90deg);
}

/* Boîte modale */
dialog.ville-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0;
  border: none;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 5px 10px rgba(0, 0, 0, 0.12);
  width: 90%;
  max-width: 500px;
  background: white;
  overflow: hidden;
}

dialog.ville-modal::backdrop {
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
}

/* Formulaire */
.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.form-group input, .form-group select {
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.form-group select optgroup {
  font-weight: 600;
  color: var(--turquoise-deep);
  background-color: var(--turquoise-pale);
  padding: 8px;
}

.form-group select option {
  padding: 8px;
  font-weight: normal;
  color: #1f2937;
  background-color: #ffffff;
}

.form-group input:focus, .form-group select:focus {
  border-color: var(--turquoise-primary);
  outline: none;
  box-shadow: 0 0 0 3px rgba(48, 199, 181, 0.15);
}

.form-group input.error, .form-group select.error {
  border-color: var(--coral-soft);
}

.error-message {
  color: var(--coral-soft);
  font-size: 12px;
  margin-top: 5px;
}

/* Boutons */
.btn {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: none;
  transition: background-color 0.2s;
}

.btn .material-icons {
  font-size: 20px;
}

.btn-primary {
  background-color: var(--turquoise-primary);
  color: white;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(48, 199, 181, 0.2);
}

.btn-primary:hover {
  background-color: var(--turquoise-deep);
  box-shadow: 0 4px 8px rgba(21, 87, 81, 0.2);
  transform: translateY(-1px);
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #4b5563;
  border: 1px solid #d1d5db;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background-color: #e5e7eb;
  color: #374151;
}

.btn-danger {
  background-color: var(--coral-soft);
  color: white;
}

.btn-danger:hover {
  background-color: #ff6a56;
}

/* Notification */
.notification {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 300px;
  max-width: 450px;
  width: 90%;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25), 0 5px 15px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
  display: flex;
  flex-direction: column;
}

.notification::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
}

.notification-content {
  padding: 25px 20px 15px;
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.notification-text {
  flex: 1;
}

.notification-text h4 {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.notification-text p {
  margin: 0;
  color: #555;
  line-height: 1.5;
}

.notification-actions {
  padding: 15px 20px;
  display: flex;
  justify-content: flex-end;
  background-color: #f9fafb;
  border-top: 1px solid #eee;
}

.notification-actions .btn {
  min-width: 80px;
}

.notification.success {
  border-left: 4px solid #9C27B0;
}

.notification.error {
  border-left: 4px solid #9C27B0;
}

.notification.warning {
  border-left: 4px solid #9C27B0;
}

.notification.info {
  border-left: 4px solid #9C27B0;
}

.notification-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification.success .material-icons {
  color: #9C27B0;
}

.notification.error .material-icons {
  color: #9C27B0;
}

.notification.warning .material-icons {
  color: #9C27B0;
}

.notification.info .material-icons {
  color: #9C27B0;
}

.notification-content {
  flex: 1;
}

.notification-content h4 {
  margin: 0 0 5px;
  font-size: 16px;
  font-weight: 600;
}

.notification-content p {
  margin: 0;
  font-size: 14px;
  color: #555;
}

.notification-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -5px;
}

.notification-close:hover {
  color: #333;
}

.warning {
  color: #d32f2f;
  font-weight: 500;
}

/* Personnalisation des barres de défilement */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: var(--turquoise-primary);
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--turquoise-deep);
}

/* Barres de défilement stylées */
::-webkit-scrollbar {
  width: 12px;
  height: 12px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: var(--turquoise-primary);
  border-radius: 10px;
}

/* Firefox */
* {
  scrollbar-width: thin;
  scrollbar-color: var(--turquoise-primary) #f1f1f1;
}

/* Styles pour la barre de recherche des sites */
.sites-search {
  margin-bottom: 15px;
}

.search-input-sites {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 4px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
}

.search-input-sites .material-icons {
  color: #777;
  margin-right: 8px;
  font-size: 18px;
}

.search-input-sites input {
  border: none;
  background: transparent;
  flex: 1;
  font-size: 14px;
  outline: none;
}

/* Styles pour la modale de site */
.site-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0;
  border: none;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 5px 10px rgba(0, 0, 0, 0.12);
  width: 90%;
  max-width: 800px;
  background: white;
  overflow: hidden;
  z-index: 1000;
}

.site-modal::backdrop {
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
}

.site-info {
  padding: 15px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.site-info h4 {
  margin: 0 0 10px;
  color: #333;
  font-size: 18px;
  font-weight: 600;
}

.site-info p {
  margin: 5px 0;
  color: #555;
  font-size: 14px;
}

.leaflet-map {
  height: 400px;
  width: 100%;
}

/* Styles pour le marqueur personnalisé */
.custom-marker-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-marker {
  color: var(--turquoise-primary);
  font-size: 24px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* Styles pour le popup de la carte */
.popup-content h4 {
  margin: 0 0 8px;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.popup-content p {
  margin: 3px 0;
  color: #555;
  font-size: 13px;
}

/* Style pour les coordonnées */
.coordinates {
  font-family: monospace;
  font-size: 12px;
  color: #666;
}

.site-status {
  font-size: 12px;
  color: #888;
  font-style: italic;
}

.site-title {
  flex: 1;
}

/* Amélioration des styles pour les sites */
.sites-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.site-item {
  cursor: pointer;
  margin-bottom: 0 !important;
  border: 1px solid #e5e7eb;
}

.site-item:hover {
  border-color: var(--turquoise-primary);
  box-shadow: 0 4px 8px rgba(48, 199, 181, 0.15);
}

/* Styles pour les boutons avec indicateurs de chargement */
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: btn-spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes btn-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Styles pour les contrôles de géolocalisation */
.location-controls {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 15px;
}

.btn-location, .btn-route, .btn-clear {
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-location {
  background-color: #3b82f6;
  color: white;
}

.btn-location:hover {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.btn-location.active {
  background-color: var(--turquoise-primary);
  color: white;
}

.btn-location.active:hover {
  background-color: var(--turquoise-deep);
}

.btn-route {
  background-color: var(--turquoise-primary);
  color: white;
}

.btn-route:hover {
  background-color: var(--turquoise-deep);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(48, 199, 181, 0.3);
}

.btn-clear {
  background-color: #ef4444;
  color: white;
}

.btn-clear:hover {
  background-color: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
}

.spinning {
  animation: spin 1s linear infinite;
}

/* Styles pour les informations d'itinéraire */
.route-info {
  margin-top: 15px;
  padding: 12px;
  background-color: #f0f9ff;
  border-radius: 6px;
  border-left: 4px solid var(--turquoise-primary);
}

.route-stats {
  display: flex;
  gap: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.stat-item .material-icons {
  font-size: 18px;
  color: var(--turquoise-primary);
}

/* Style pour l'icône de voiture */
.car-icon {
  font-size: 16px !important;
  color: #6b7280 !important;
  margin-left: 6px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.car-icon:hover {
  opacity: 1;
}
</style>
